import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { buyerQueryHistoryService } from '../../../services/buyerService';

const QueryHistoryTab = ({ language }) => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const content = {
    en: {
      title: 'Query History',
      noQueries: 'No queries found',
      loading: 'Loading queries...',
      date: 'Date',
      type: 'Type',
      message: 'Message',
      status: 'Status',
      pending: 'Pending',
      responded: 'Responded',
      completed: 'Completed'
    },
    zh: {
      title: '查询历史',
      noQueries: '未找到查询',
      loading: '加载查询中...',
      date: '日期',
      type: '类型',
      message: '消息',
      status: '状态',
      pending: '待处理',
      responded: '已回复',
      completed: '已完成'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    const controller = new AbortController();
    loadQueries(controller.signal);
    return () => controller.abort();
  }, []);

  const loadQueries = async (signal) => {
    try {
      setLoading(true);
      setError('');
      const data = await buyerQueryHistoryService?.getAll();
      if (signal?.aborted) return;
      setQueries(data);
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.message !== 'signal is aborted without reason') {
        setError(err?.message || 'Failed to load queries');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      responded: 'text-primary',
      completed: 'text-success'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const getStatusBg = (status) => {
    const colors = {
      pending: 'bg-warning/10',
      responded: 'bg-primary/10',
      completed: 'bg-success/10'
    };
    return colors?.[status] || 'bg-muted';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">{t?.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-4" />
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-heading font-bold text-foreground">{t?.title}</h2>

      {queries?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noQueries}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {queries?.map((query) => (
            <div key={query?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm text-muted-foreground">{t?.date}</p>
                  <p className="text-sm">{new Date(query?.createdAt)?.toLocaleDateString()}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBg(query?.status)} ${getStatusColor(query?.status)}`}>
                  {t?.[query?.status] || query?.status?.toUpperCase()}
                </span>
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-sm text-muted-foreground">{t?.type}</p>
                  <p className="font-medium">{query?.inquiryType}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">{t?.message}</p>
                  <p className="text-sm">{query?.message}</p>
                </div>

                {query?.countryInterest && (
                  <div>
                    <p className="text-sm text-muted-foreground">Country Interest</p>
                    <p className="text-sm">{query?.countryInterest}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QueryHistoryTab;